import { Controller, UseFormSetValue } from 'react-hook-form'
import { CheckboxGroup, CheckboxType } from '../../../../components'
import {
  videoFormatAddonOptions,
  aspectRatioOptions,
  mapAddonToAspectRatio,
} from '../../../../utils/mappers'
import { AspectRatio, Currency, CampaignAddon } from '../../../../gql/types'
import { PricingData, getVideoPricing } from '../../../../utils/PricingUtil'
import { useEffect, useRef } from 'react'
import { RadioGroup } from '../../../../components'

interface Props {
  errors: any
  register: any
  control: any
  watch: any
  currency: Currency
  pricing: PricingData
  setValue: UseFormSetValue<any>
}

const EditVideoAspectRatio = ({
  errors,
  watch,
  control,
  register,
  currency,
  pricing,
  setValue,
}: Props) => {
  const videoPricing = getVideoPricing({
    currency: currency,
    videoDuration: watch('videoDuration'),
    pricing,
  })

  const addonOptions = videoFormatAddonOptions(videoPricing.addons, currency)
  const selectedDefault = watch('defaultAspectRatio')
  const currentAddons = watch('addonsAspectRatios') || []
  const prevSelectedDefault = useRef(selectedDefault)

  // Calculate filtered options directly from props without useState
  // This ensures we always show the correct options excluding the selected default
  const filteredAddonOptions = addonOptions.filter(
    (addonOption) =>
      mapAddonToAspectRatio(addonOption.type) !== selectedDefault,
  )

  useEffect(() => {
    if (prevSelectedDefault.current !== selectedDefault) {
      // When landscape is selected, clear all addons as they're not allowed
      if (selectedDefault === AspectRatio.landscape) {
        setValue('addonsAspectRatios', [])
      } else {
        // For other aspect ratios, only remove the one selected as default
        const updatedAddons = currentAddons.filter(
          (addon: CampaignAddon) =>
            mapAddonToAspectRatio(addon) !== selectedDefault,
        )

        // Only update if there's actually something to remove
        if (currentAddons.length !== updatedAddons.length) {
          setValue('addonsAspectRatios', updatedAddons)
        }
      }

      // Update the ref to track the next change
      prevSelectedDefault.current = selectedDefault
    }
  }, [selectedDefault])

  return (
    <div>
      <div className='mt-10 sm:mt-0'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <div className=''>
              <h3 className='text-lg font-semibold font-sans leading-6 text-gray-900'>
                Video format (aspect ratio)
              </h3>
              <p className='mt-1 text-sm font-normal font-sans leading-5 text-gray-500'>
                Choose your preferred video format. Additional formats can be
                purchased as add-ons.
              </p>
            </div>
          </div>
          <div className='mt-5 md:col-span-2 md:mt-0'>
            <div className='md:shadow rounded-t-md sm:overflow-hidden'>
              <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                <div className='grid gap-6'>
                  <div className='col-span-3 sm:col-span-2'>
                    <Controller
                      name='defaultAspectRatio'
                      control={control}
                      rules={{
                        required: 'Please select a default aspect ratio',
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          label='Default'
                          options={aspectRatioOptions}
                          className='grid grid-cols-2'
                          field={field}
                          error={errors?.defaultAspectRatio?.message}
                        />
                      )}
                    />

                    {selectedDefault !== AspectRatio.landscape && (
                      <p className='mt-4 text-sm leading-5 font-medium text-gray-700'>
                        Additional Formats
                      </p>
                    )}

                    {selectedDefault === AspectRatio.landscape && (
                      <p className='mt-4 text-sm leading-5 font-medium text-gray-700'>
                        No additional formats available for landscape videos
                        (16:9). Due to the significant difference between
                        landscape and the other formats, creators cannot easily
                        adapt between them. For both formats, please create
                        separate campaigns.
                      </p>
                    )}

                    {selectedDefault !== AspectRatio.landscape && (
                      <CheckboxGroup
                        selected={watch('addonsAspectRatios')}
                        {...register('addonsAspectRatios')}
                        className='grid grid-cols-2'
                        error={errors?.addonsAspectRatios?.message}
                      >
                        {filteredAddonOptions.map(
                          ({ name, type, description, icon }, index) => {
                            return (
                              <CheckboxGroup.CheckboxItem
                                type={CheckboxType.checkbox}
                                key={index}
                                name={type}
                                value={type}
                                title={name}
                                icon={icon}
                                subtitle={description}
                                {...register('addonsAspectRatios')}
                                className='!my-2 !mr-2'
                                inputView={false}
                              />
                            )
                          },
                        )}
                      </CheckboxGroup>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='hidden sm:block' aria-hidden='true'>
        <div className='py-5'>
          <div className='border-t border-gray-200' />
        </div>
      </div>
    </div>
  )
}

export { EditVideoAspectRatio }
